import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

/**
 * NotificationCenter — generic trigger-with-badge + fixed-width popover panel
 * shell (sticky header, capped scroll list, empty state). Extraction of the
 * composition duplicated at BulkJobsDrawer/index.tsx:75-151 and
 * GlobalDownload.tsx:195-344. Pure shell: NO domain logic, NO data fetching.
 *
 * Built on the Radix `ui/popover` primitive (plan decision D1). The content
 * layer MUST stay `z-[200]` — stock popover ships `z-50`, which Chariot's
 * stacked drawers/dialogs would cover (matches `select`/`dropdown-menu`).
 */

type NotificationCenterProps = React.ComponentProps<typeof Popover>;

/** Root: controlled `open`/`onOpenChange` pass-through over Popover. */
function NotificationCenter(props: NotificationCenterProps) {
  return <Popover {...props} />;
}
NotificationCenter.displayName = 'NotificationCenter';

/**
 * Trigger slot (asChild-capable). `relative` anchors an absolutely
 * positioned NotificationCenterBadge over the trigger's corner.
 */
const NotificationCenterTrigger = React.forwardRef<
  React.ComponentRef<typeof PopoverTrigger>,
  React.ComponentPropsWithoutRef<typeof PopoverTrigger>
>(({ className, ...props }, ref) => (
  <PopoverTrigger ref={ref} className={cn('relative', className)} {...props} />
));
NotificationCenterTrigger.displayName = 'NotificationCenterTrigger';

// Dedicated cva family (precedent: `statusBadgeVariants` lives beside
// `badgeVariants` in badge.tsx rather than composing it — the chip base's
// bg-primary/shadow/hover classes would all need twMerge overrides here).
// Default accent is the badge-scoped T2 info token
// `bg-chariot-stroke-blue-strong` (#2563EB in both themes — white text
// 5.17:1 AA; the base dark-theme `stroke-blue` #3B82F6 is only 3.68:1) —
// never red.
const notificationCenterBadgeVariants = cva(
  'pointer-events-none absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold text-white',
  {
    variants: {
      variant: {
        default: 'bg-chariot-stroke-blue-strong',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

interface NotificationCenterBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof notificationCenterBadgeVariants> {
  /** Hidden when <= 0; renders "9+" when > 9. */
  count: number;
}

const NotificationCenterBadge = React.forwardRef<
  HTMLSpanElement,
  NotificationCenterBadgeProps
>(({ count, className, variant, ...props }, ref) => {
  if (count <= 0) return null;
  return (
    <span
      ref={ref}
      className={cn(notificationCenterBadgeVariants({ variant }), className)}
      {...props}
    >
      {count > 9 ? '9+' : count}
    </span>
  );
});
NotificationCenterBadge.displayName = 'NotificationCenterBadge';

/**
 * Panel: `z-[200]` drawer-safe layer, fixed 380px width, flush padding.
 *
 * The panel itself is the height backstop: stacked sections can exceed a
 * short viewport (each `NotificationCenterList` caps only its own list), and
 * Radix collision handling repositions but never shrinks content — so the
 * panel caps at the popover's available height and scrolls, keeping the
 * lower sections reachable. `NotificationCenterHeader` is `sticky` against
 * exactly this scroll container.
 */
const NotificationCenterContent = React.forwardRef<
  React.ComponentRef<typeof PopoverContent>,
  React.ComponentPropsWithoutRef<typeof PopoverContent>
>(({ className, align = 'end', sideOffset = 8, ...props }, ref) => (
  <PopoverContent
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    className={cn(
      'z-[200] max-h-[var(--radix-popover-content-available-height)] w-[380px] overflow-y-auto p-0',
      className
    )}
    {...props}
  />
));
NotificationCenterContent.displayName = 'NotificationCenterContent';

const NotificationCenterHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'border-default bg-popover sticky top-0 z-10 flex items-center justify-between border-b px-4 py-3',
      className
    )}
    {...props}
  />
));
NotificationCenterHeader.displayName = 'NotificationCenterHeader';

const NotificationCenterList = React.forwardRef<
  React.ComponentRef<typeof ScrollArea>,
  React.ComponentPropsWithoutRef<typeof ScrollArea>
>(({ className, children, ...props }, ref) => (
  <ScrollArea ref={ref} className={cn('max-h-[400px]', className)} {...props}>
    <div className="divide-chariot-stroke divide-y">{children}</div>
  </ScrollArea>
));
NotificationCenterList.displayName = 'NotificationCenterList';

const NotificationCenterItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-start gap-3 px-4 py-3', className)}
    {...props}
  />
));
NotificationCenterItem.displayName = 'NotificationCenterItem';

const NotificationCenterEmpty = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-chariot-text-secondary flex flex-col items-center justify-center gap-2 py-10 text-sm',
      className
    )}
    {...props}
  />
));
NotificationCenterEmpty.displayName = 'NotificationCenterEmpty';

export {
  NotificationCenter,
  NotificationCenterBadge,
  NotificationCenterContent,
  NotificationCenterEmpty,
  NotificationCenterHeader,
  NotificationCenterItem,
  NotificationCenterList,
  NotificationCenterTrigger,
};
export type { NotificationCenterBadgeProps, NotificationCenterProps };
